import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BbTableService, BbTableSelectOption, TableRow, TableColumn, TableConfig } from '../../../services/bb-table.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';

@Component({
	selector: 'bb-table-header',
	templateUrl: './bb-table-header.component.html',
	styleUrls: ['./bb-table-header.component.scss']
})

export class BbTableHeaderComponent implements OnInit, OnDestroy {
	@Input() configurable: boolean;
	@Input() theme: string;

	multiSelect: boolean;
	allSelected: boolean;
	amountOfRows = 0;
	limit = 0;
	configVisible = false;
	columns: TableColumn[] = [];
	amountOfVisibleColumns: number;

	tableConfig: TableConfig;
	configColumns: TableColumn[] = []; // columns for config panel

	subscriptions: Subscription;

	limitChanged: Subject<void>;

	constructor(private bbTableService: BbTableService) {
		this.limitChanged = new Subject<void>();
		this.subscriptions = new Subscription();
	}

	ngOnInit() {
		this.limit = this.bbTableService.limit;
		this.multiSelect = (this.bbTableService.selectOption === BbTableSelectOption.multi);

		this.subscriptions.add(this.bbTableService.tableConfig.subscribe((c) => {
			this.tableConfig = c;
		}));

		this.subscriptions.add(this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => this.amountOfRows = rows.length));

		this.subscriptions.add(this.bbTableService.rowsSelectedObservable.subscribe((selectedRows: TableRow[]) => {
			this.allSelected = (selectedRows.length > 0 && selectedRows.length === this.amountOfRows);
		}));

		this.subscriptions.add(this.bbTableService.limitChangedObservable.subscribe((limit: number) => {
			this.limit = limit;
		}));

		// Hide configuration popup when sorting
		this.subscriptions.add(this.bbTableService.rowsSortedObservable.subscribe(() => {
			this.configVisible = false;
		}));

		// Hide configuration popup when changing offset
		this.subscriptions.add(this.bbTableService.offsetChangedObservable.subscribe(() => {
			this.configVisible = false;
		}));

		this.subscriptions.add(this.bbTableService.columnsChangedObservable
			.subscribe((columns: TableColumn[]) => {
				this.columns = columns;
				this.configColumns = columns.filter(c => c.label);

				let amountOfVisibleColumns = 0;

				columns.forEach((column: TableColumn) => {
					if (column.visible) {
						amountOfVisibleColumns++;
					}
				});

				this.amountOfVisibleColumns = amountOfVisibleColumns;
			}));

		if (this.configurable === undefined) {
			this.configurable = this.bbTableService.isConfigurable();
		}

		this.subscriptions.add(this.limitChanged
			.pipe(debounceTime(400))
			.subscribe(() => {
				this.limit = Math.min(100, this.limit);
				this.bbTableService.setLimit(this.limit);
				this.bbTableService.storeConfig();
			})
		);
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	selectAllRows() {
		this.bbTableService.selectAllRows();
	}

	resetLimit() {
		this.bbTableService.resetLimit();
	}

	resetColumns() {
		this.bbTableService.resetColumns();
	}

	columnChecked(visible: any, column: TableColumn) {
		if (this.theme === 'socials-theme') {
			visible = visible.checked;
		}
		this.bbTableService.setColumnVisibility(column.property, visible);
	}

	reorder(ev: CdkDragDrop<string[]>) {
		const newOrder = bbDeepCopy(this.configColumns);
		moveItemInArray(newOrder, ev.previousIndex, ev.currentIndex);
		const columnNames = newOrder.map(c => c.property);

		// store new column order in the config
		this.bbTableService.storeConfig(columnNames);
	}
}
